/**
 * PriceChart Component
 *
 * T145-T150: Historical price chart component for User Story 5
 * Displays line chart for historical prices with time range selector
 * NOW THEME-AWARE
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import { LineChart } from 'react-native-chart-kit';
import { getToken } from '../../services/tokenStorage';
import { financialDataAPI } from '../../services/api/financialDataApi';
import { useTheme } from '../../contexts/ThemeContext';

interface PriceChartProps {
  instrument_id: number;
  currency?: string;
}

type TimeRange = '1M' | '3M' | '1Y' | '3Y' | '5Y';

const TIME_RANGES: { label: TimeRange; days: number }[] = [
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '1Y', days: 365 },
  { label: '3Y', days: 365 * 3 },
  { label: '5Y', days: 365 * 5 },
];

export function PriceChart({ instrument_id, currency = 'USD' }: PriceChartProps) {
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [selectedRange, setSelectedRange] = useState<TimeRange>('1Y');
  const [prices, setPrices] = useState<number[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPrices();
  }, [instrument_id, selectedRange]);

  const fetchPrices = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getToken();
      const range = TIME_RANGES.find((r) => r.label === selectedRange);

      if (!range) return;

      // Calculate from_date based on selected range
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - range.days);

      const response = await financialDataAPI.getHistoricalPrices(
        instrument_id,
        token,
        undefined, // limit
        fromDate.toISOString().split('T')[0], // from_date YYYY-MM-DD
        toDate.toISOString().split('T')[0] // to_date YYYY-MM-DD
      );

      if (response.prices && Array.isArray(response.prices)) {
        // Reverse prices to show oldest to newest (left to right on chart)
        const reversedPrices = [...response.prices].reverse();

        // Extract close prices for chart
        const closePrices = reversedPrices.map((p) => p.close || 0);

        // Create date labels (show every Nth label to avoid crowding)
        const numLabels = Math.min(6, reversedPrices.length);
        const step = Math.ceil(reversedPrices.length / numLabels);
        const dateLabels = reversedPrices
          .filter((_, index) => index % step === 0)
          .map((p) => {
            const date = new Date(p.date);
            return `${date.getMonth() + 1}/${date.getDate()}`;
          });

        setPrices(closePrices);
        setLabels(dateLabels);
      } else {
        setPrices([]);
        setLabels([]);
      }
    } catch (err: any) {
      console.error('Error fetching historical prices:', err);
      setError(err.message || 'Failed to fetch price data');
      setPrices([]);
      setLabels([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.surface }]}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: theme.colors.text }]}>Historical Prices</Text>
          <ActivityIndicator size="small" color={theme.colors.primary} />
        </View>
      </View>
    );
  }

  if (error || prices.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.surface }]}>
        <Text style={[styles.title, { color: theme.colors.text }]}>Historical Prices</Text>
        <Text style={[styles.errorText, { color: theme.colors.error }]}>
          {error || 'No price data available'}
        </Text>
      </View>
    );
  }

  // Calculate min/max for Y-axis
  const minPrice = Math.min(...prices) * 0.95; // 5% buffer
  const maxPrice = Math.max(...prices) * 1.05; // 5% buffer

  // Theme-aware chart configuration
  const chartConfig = {
    backgroundColor: theme.colors.surface,
    backgroundGradientFrom: theme.colors.surface,
    backgroundGradientTo: theme.colors.surface,
    decimalPlaces: 2,
    color: (opacity = 1) => {
      const rgb = theme.id === 'bauhaus' ? '16, 64, 192' : '223, 225, 4';
      return `rgba(${rgb}, ${opacity})`;
    },
    labelColor: (opacity = 1) => {
      const rgb = theme.id === 'bauhaus' ? '18, 18, 18' : '250, 250, 250';
      return `rgba(${rgb}, ${opacity})`;
    },
    style: {
      borderRadius: 16,
    },
    propsForDots: {
      r: '4',
      strokeWidth: '2',
      stroke: theme.id === 'bauhaus' ? theme.colors.secondary : theme.colors.primary,
    },
  };

  const screenWidth = Dimensions.get('window').width - 32; // 16px padding on each side

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.surface }]}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: theme.colors.text }]}>Historical Prices</Text>

        {/* Time range selector */}
        <View style={styles.rangeSelector}>
          {TIME_RANGES.map((range) => (
            <TouchableOpacity
              key={range.label}
              onPress={() => setSelectedRange(range.label)}
              style={[
                styles.rangeButton,
                {
                  backgroundColor:
                    selectedRange === range.label
                      ? theme.colors.primary
                      : theme.colors.backgroundElevated,
                },
              ]}
            >
              <Text
                style={[
                  styles.rangeButtonText,
                  {
                    color:
                      selectedRange === range.label ? theme.colors.textInverse : theme.colors.text,
                  },
                ]}
              >
                {range.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Price chart */}
      <LineChart
        data={{
          labels,
          datasets: [
            {
              data: prices,
            },
          ],
        }}
        width={screenWidth}
        height={220}
        chartConfig={chartConfig}
        bezier
        style={styles.chart}
        withDots={prices.length <= 30} // Only show dots for smaller datasets
        withInnerLines={false}
        withOuterLines={true}
        withVerticalLines={false}
        withHorizontalLines={true}
        segments={5}
        formatYLabel={(value) => `$${Number(value).toFixed(0)}`}
      />

      {/* Currency note for international stocks */}
      {currency !== 'USD' && (
        <Text style={[styles.currencyNote, { color: theme.colors.textTertiary }]}>
          Prices displayed in {currency}
        </Text>
      )}
    </View>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      borderRadius: theme.radii.lg,
      padding: theme.spacing.md,
      marginVertical: theme.spacing.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    header: {
      marginBottom: theme.spacing.md,
    },
    title: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.md,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    rangeSelector: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
    },
    rangeButton: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: theme.radii.full,
      overflow: 'hidden',
    },
    rangeButtonText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    chart: {
      marginVertical: theme.spacing.sm,
      borderRadius: theme.radii.lg,
    },
    currencyNote: {
      fontSize: theme.typography.fontSizeXs,
      fontStyle: 'italic',
      marginTop: theme.spacing.sm,
      textAlign: 'center',
    },
    errorText: {
      fontSize: theme.typography.fontSizeSm,
      textAlign: 'center',
      paddingVertical: 20,
    },
    loadingText: {
      fontSize: theme.typography.fontSizeSm,
      textAlign: 'center',
    },
  });
}
